<template>
    <div
        class="my-3 promotion-card"
        :class="{ claimed: promotion.claimed }"
    >
        <!-- CLAIMED BADGE -->
        <div
            class="claimed-logo"
            v-if="promotion.claimed"
        >
            <img
                src="../../../../assets/claimed.png"
                alt="Claimed"
                width="200"
            >
        </div>

        <!-- IMAGE -->
        <img
            :src="promotion.image"
            alt="Promotion Image"
            class="promotion-image"
        >

        <!-- TITLE + BUTTON -->
        <div class="card-bottom d-flex justify-content-between align-items-center">
            <h3 class="title">{{ promotion.name }}</h3>

            <button
                class="btn detail-btn"
                @click="openOverlay = true"
            >
                {{ $t('detail') }}
            </button>
        </div>

        <!-- OVERLAY -->
        <van-overlay
            :show="openOverlay"
            @click="openOverlay = false"
        >
            <div
                class="overlay-wrapper"
                @click.stop
            >
                <div class="detail-card">

                    <!-- CLOSE BUTTON -->
                    <van-icon
                        name="cross"
                        class="close-btn"
                        @click="openOverlay = false"
                    />

                    <h3 class="title mb-2">{{ promotion.name }}</h3>

                    <span v-html="promotion.description"></span>

                    <div class="mb-2">
                        <div class="info-row">
                            <span>{{ $t('promotion.daily_usage_limit') }}</span>
                            <span>{{ promotion.daily_usage_limit ?? 'Unlimited' }}</span>
                        </div>

                        <div class="info-row">
                            <span>{{ $t('promotion.bonus_percent') }}</span>
                            <span>{{ promotion.promotion_percent ?? '-' }} %</span>
                        </div>

                        <div class="info-row">
                            <span>{{ $t('promotion.minimum_turnover_amount') }}</span>
                            <span>{{ promotion.minimum_turn_over_amount ?? '-' }} MMK</span>
                        </div>

                        <div class="info-row">
                            <span>{{ $t('promotion.minimum_withdraw_times') }}</span>
                            <span>{{ promotion.minimum_withdraw_times ?? '-' }}</span>
                        </div>

                        <!-- PROVIDER RESTRICTION BOX -->
                        <div
                            v-if="promotion.providers && promotion.providers.length"
                            class="provider-section"
                        >
                            <div class="restriction-box">
                                <van-icon
                                    name="warning"
                                    class="warning-icon"
                                />
                                <div class="warning-text">
                                    <!-- While this promotion is active, you can <strong>only play</strong> these providers: -->
                                    <span v-html="$t('promotion.warning_text')"></span>
                                </div>
                            </div>

                            <div
                                v-for="provider in promotion.providers"
                                :key="provider.id"
                                class="provider-pill"
                            >
                                {{ provider.name }}
                            </div>
                        </div>

                    </div>

                    <button
                        class="btn claim-btn"
                        :disabled="promotion.claimed"
                        @click="claimPromotion(promotion)"
                    >
                        {{ promotion.claimed ? $t('promotion.claimed') : $t('promotion.claim') }}
                    </button>
                </div>
            </div>
        </van-overlay>

    </div>
</template>


<script>
import { Dialog } from 'vant';
import { mapActions } from 'vuex';

export default {
    props: {
        promotion: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            openOverlay: false
        }
    },
    methods: {
        ...mapActions('promotions', ['fetchPromotions']),

        async claimPromotion(promotion) {
            this.openOverlay = false;
            Dialog.confirm({
                title: this.$t('promotion.confirm_title'),
                message: this.$t('promotion.confirm_message'),
                confirmButtonText: this.$t('okay'),
                cancelButtonText: this.$t('cancel')
            }).then(async () => {
                try {
                    const response = await axios.post(
                        `/user/promotions/${promotion.id}/claim`,
                        {},
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem('token')}`,
                            },
                        }
                    );

                    // console.log("CLAIM RESPONSE", response);
                    this.fetchPromotions();
                    this.$toast.success('Success!');
                } catch (error) {
                    this.$toast.fail('Failed to update status');
                }
            }).catch(() => {
            });
        }
    },
}
</script>

<style scoped>
.provider-section {
    margin-top: 20px;
}

/* Warning box (unchanged) */
.restriction-box {
    background: rgba(255, 193, 7, 0.15);
    border-left: 3px solid #ffc107;
    padding: 10px 12px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    margin-bottom: 12px;
}

.warning-icon {
    color: #ffc107;
    font-size: 18px;
    margin-right: 8px;
}

.warning-text {
    color: #f5f5f5;
    font-size: 14px;
}

/* ==== COMPACT PILL STYLES ==== */
.provider-pill {
    padding: 4px 10px;
    font-size: 12px;
    background: rgba(72, 52, 255, 0.18);
    border: 1px solid #4834ff;
    border-radius: 12px;
    color: #fff;
    display: inline-block;
    margin: 4px;
    white-space: nowrap;
    transition: 0.2s ease;
}

.provider-pill:hover {
    background: rgba(72, 52, 255, 0.3);
    transform: translateY(-1px);
}



.promotion-card {
    position: relative;
    background: #1a183d;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #2b2960;
    box-shadow: 0 4px 14px rgba(0, 0, 0, .35);
    transition: transform 0.25s ease;
}

.promotion-card.claimed::before {
    content: "";
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1;
}

.claimed-logo {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.promotion-image {
    width: 100%;
    height: 150px;
    object-fit: cover;
    border-bottom: 1px solid #2b2960;
}

.card-bottom {
    padding: 14px 16px;
    z-index: 3;
}

.title {
    color: #fff;
    margin: 0;
    font-weight: 600;
}

.detail-btn {
    background: #4834ff;
    color: #fff;
    border-radius: 6px;
    padding: 6px 14px;
    font-size: 14px;
    border: none;
}

/* ==== INFO ROW ==== */
.info-row {
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    border-bottom: 1px solid #2b2960;
    font-size: 14px;
}

.info-row span:first-child {
    color: #aaa;
}

.info-row span:last-child {
    color: #fff;
}

/* Overlay center */
.overlay-wrapper {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
}

/* Scrollable card */
.detail-card {
    max-width: 500px;
    background: #202124;
    color: #fff;
    border-radius: 12px;
    padding: 20px;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
}

/* Close icon */
.close-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    font-size: 16px;
    cursor: pointer;
    color: #fff;
}


/* ==== CLAIM BUTTON ==== */
.claim-btn {
    width: 100%;
    background: #4834ff;
    color: #fff;
    border-radius: 8px;
    padding: 10px;
    font-weight: bold;
    border: none;
}

.claim-btn:disabled {
    background: #444;
    opacity: .6;
}
</style>